"use client";

import { useState, type ReactNode } from "react";

interface InlineNumberEditProps {
  /** What the value reads as when not editing. */
  display: ReactNode;
  /** Starting text for the input. */
  initial: string;
  /** Accessible name for the edit button and input. */
  label: string;
  /** The input's width class (e.g. `w-16`), the text before it (e.g. a currency symbol) and the phone keyboard. */
  field?: { widthClass?: string; prefix?: string; inputMode?: "decimal" | "numeric" | "text" };
  /** Called with the trimmed text on Enter or blur; return false to keep editing. */
  onCommit: (text: string) => boolean | void;
}

/**
 * A value that turns into a small input when clicked: Enter or blur saves, Escape cancels.
 * @param props - Display, starting text, label, width, prefix and commit handler.
 * @returns The control.
 * @throws Never.
 */
export const InlineNumberEdit = ({ display, initial, label, field = {}, onCommit }: InlineNumberEditProps) => {
  const { widthClass = "w-16", prefix, inputMode = "decimal" } = field;

  // States
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(initial);

  // Handlers
  const commit = () => {
    if (text.trim() === initial.trim()) return setEditing(false);
    if (onCommit(text.trim()) !== false) setEditing(false);
  };

  // Render
  if (!editing) {
    return (
      // ui-lint-disable-next-line raw-control: a custom control (segment, chip, calendar day, stepper, icon or inline editor) styled to its row; FetchButton's sizes and 3D treatment don't fit
      <button
        type="button"
        aria-label={label}
        onClick={() => {
          setText(initial);
          setEditing(true);
        }}
        className="-mx-1 inline-flex cursor-pointer items-center rounded-md px-1 underline decoration-transparent decoration-dotted underline-offset-[3px] transition-colors hover:bg-gray-100 hover:decoration-gray-500 focus-visible:decoration-gray-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 [@media(hover:none)]:decoration-gray-300"
      >
        {/* At rest it reads as a plain value; hover or focus shows the dotted underline and background
            that say it can be typed over. Touch, with no hover, keeps the underline. */}
        {display}
      </button>
    );
  }

  return (
    <span className="inline-flex h-7 items-center gap-1 rounded-md border border-indigo-300 bg-white px-1.5">
      {prefix && <span className="text-xs font-medium text-gray-600">{prefix}</span>}
      <input
        autoFocus
        aria-label={label}
        value={text}
        inputMode={inputMode}
        onChange={(event) => setText(event.target.value)}
        onBlur={commit}
        onKeyDown={(event) => {
          if (event.key === "Enter") event.currentTarget.blur();
          if (event.key === "Escape") setEditing(false);
        }}
        className={`${widthClass} bg-transparent text-right text-base font-medium tabular-nums text-gray-900 focus:outline-none md:text-sm`}
      />
    </span>
  );
};
